import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { progressStepService } from '../services/progressStepService';
import { videoService, Video } from '../services/videoService';
import { ProgressStep } from '../types/progressStep';
import { toast } from 'react-toastify';
import RichTextEditor from './RichTextEditor';
import PhotoGallery from './PhotoGallery';
import VideoGallery from './VideoGallery';
import '../styles/AddItemsModal.css';
import '../styles/DrawerForm.css';

interface ProgressStepModalProps {
  workspaceId: string;
  projectId: string;
  step: ProgressStep | null;
  onClose: () => void;
  onSave: (createdStep?: ProgressStep) => void;
}

type TabType = 'basic' | 'gallery' | 'video';

const ProgressStepModal: React.FC<ProgressStepModalProps> = ({
  workspaceId,
  projectId,
  step,
  onClose,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [loading, setLoading] = useState(false);

  // Form fields
  const [stepNumber] = useState(step?.stepNumber || 0); // Auto-generated, read-only
  const [time, setTime] = useState(step?.time || '');
  const [description, setDescription] = useState(step?.description || '');
  const [status, setStatus] = useState(step?.status || '');
  const [isPublished, setIsPublished] = useState(step?.isPublished || false);
  const [isCompleted, setIsCompleted] = useState(step?.isCompleted || false);

  // Video state
  const [videos, setVideos] = useState<Video[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  // Fetch videos when step is loaded
  useEffect(() => {
    const fetchVideos = async () => {
      if (step?._id) {
        setLoadingVideos(true);
        try {
          const fetchedVideos = await videoService.getVideos(
            workspaceId,
            'progress_step',
            step._id
          );
          setVideos(fetchedVideos);
        } catch (error) {
          console.error('Error fetching videos:', error);
          toast.error('Failed to load videos');
        } finally {
          setLoadingVideos(false);
        }
      }
    };

    fetchVideos();
  }, [step?._id, workspaceId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      const data = {
        ...(step && stepNumber ? { stepNumber } : {}), // Only include stepNumber when editing
        time,
        description,
        status,
        isPublished,
        isCompleted
      };

      if (step) {
        await progressStepService.update(workspaceId, step._id, data);
        toast.success('Progress step updated successfully');
        // Don't close modal after update - let user continue editing/adding photos
      } else {
        const createdStep = await progressStepService.create(workspaceId, projectId, data);
        toast.success('Progress step created successfully');
        // Notify parent to refresh list and stay in edit mode with the new step
        onSave(createdStep);
      }
    } catch (err: any) {
      console.error('Error saving progress step:', err);
      toast.error(err.response?.data?.message || 'Failed to save progress step');
    } finally {
      setLoading(false);
    }
  };

  // Video handlers
  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    if (!step?._id) return;

    try {
      const newVideo = await videoService.createVideo(
        workspaceId,
        'progress_step',
        step._id,
        {
          title: video.title,
          url: video.url,
          type: video.type,
          thumbnail: video.thumbnail
        }
      );
      setVideos(prev => [...prev, newVideo]);
      toast.success('Video added successfully');
    } catch (error: any) {
      console.error('Error adding video:', error);
      toast.error(error.response?.data?.message || 'Failed to add video');
      throw error; // Re-throw to let VideoGallery handle it
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    if (!step?._id) return;

    try {
      await videoService.deleteVideo(workspaceId, videoId);
      setVideos(prev => prev.filter(v => v._id !== videoId));
      toast.success('Video deleted successfully');
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error(error.response?.data?.message || 'Failed to delete video');
      throw error; // Re-throw to let VideoGallery handle it
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    if (!step?._id) return;

    try {
      await videoService.updateVideo(workspaceId, videoId, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (error: any) {
      console.error('Error updating video:', error);
      toast.error(error.response?.data?.message || 'Failed to update video');
      throw error;
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <h3>{step ? 'Edit Progress Step' : 'Add Progress Step'}</h3>
          <button className="modal-close-btn" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 24px',
          background: '#D4CBC4',
          borderRadius: '20px',
          margin: '16px 24px 0'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            style={{
              padding: '10px 24px',
              border: 'none',
              background: activeTab === 'basic' ? '#48182F' : 'transparent',
              color: activeTab === 'basic' ? '#FFFFFF' : '#48182F',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              outline: 'none'
            }}
          >
            Basic
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('gallery')}
            style={{
              padding: '10px 24px',
              border: 'none',
              background: activeTab === 'gallery' ? '#48182F' : 'transparent',
              color: activeTab === 'gallery' ? '#FFFFFF' : '#48182F',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              outline: 'none'
            }}
          >
            Gallery
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('video')}
            style={{
              padding: '10px 24px',
              border: 'none',
              background: activeTab === 'video' ? '#48182F' : 'transparent',
              color: activeTab === 'video' ? '#FFFFFF' : '#48182F',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              outline: 'none'
            }}
          >
            Video
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div className="modal-body" style={{ flex: 1, overflow: 'auto' }}>
            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Step & Time Row */}
                <div style={{ display: 'grid', gridTemplateColumns: step ? '1fr 2fr' : '1fr', gap: '16px' }}>
                  {step && (
                    <div className="form-group">
                      <label htmlFor="stepNumber" className="form-label">
                        Step:
                      </label>
                      <input
                        type="number"
                        id="stepNumber"
                        className="form-input"
                        value={stepNumber}
                        disabled
                        style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="time" className="form-label">
                      Time:
                    </label>
                    <input
                      type="text"
                      id="time"
                      className="form-input"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      disabled={loading}
                      placeholder="e.g., January 2021"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description:
                  </label>
                  <RichTextEditor
                    content={description}
                    onChange={setDescription}
                    placeholder="Type something"
                  />
                </div>

                {/* Status, Publish & Complete Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '3fr 120px 120px', gap: '16px', alignItems: 'end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="status" className="form-label">
                      Status:
                    </label>
                    <input
                      type="text"
                      id="status"
                      className="form-input"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={loading}
                      placeholder="e.g., Complete, In progress, Upcomming"
                    />
                  </div>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    height: '40px',
                    marginBottom: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                      disabled={loading}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '14px', color: '#48182F', whiteSpace: 'nowrap' }}>Publish</span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    height: '40px',
                    marginBottom: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={(e) => setIsCompleted(e.target.checked)}
                      disabled={loading}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '14px', color: '#48182F', whiteSpace: 'nowrap' }}>Complete</span>
                  </label>
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && step && (
              <div>
                <PhotoGallery
                  workspaceId={workspaceId}
                  entityType="progress_step"
                  entityId={step._id}
                  imageType="photo_gallery"
                  allowMultiple={true}
                />
              </div>
            )}

            {activeTab === 'gallery' && !step && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                color: '#6B7280'
              }}>
                <p>Please save the progress step first before adding photos.</p>
              </div>
            )}

            {/* Video Tab */}
            {activeTab === 'video' && step && (
              <div>
                <VideoGallery
                  workspaceId={workspaceId}
                  entityType="progress_step"
                  entityId={step._id}
                  videos={videos}
                  onAddVideo={handleAddVideo}
                  onDeleteVideo={handleDeleteVideo}
                  onUpdateVideo={handleUpdateVideo}
                  hideHeader={false}
                />
              </div>
            )}

            {activeTab === 'video' && !step && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                color: '#6B7280'
              }}>
                <p>Please save the progress step first before adding videos.</p>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-primary btn-md ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? '' : (step ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgressStepModal;
